import 'ol/ol.css';
import React from 'react';
import { Color } from 'ol/color';
import { getCenter } from 'ol/extent';
import ImageLayer from 'ol/layer/Image';
import GeoJSON from 'ol/format/GeoJSON';
import VectorLayer from 'ol/layer/Vector';
import { Col, Row } from 'react-bootstrap';
import Static from 'ol/source/ImageStatic';
import VectorSource from 'ol/source/Vector';
import { Feature, Overlay, View } from 'ol';
import ImageStatic from 'ol/source/ImageStatic';
import { fromLonLat, Projection } from 'ol/proj';
import { Polygon, SimpleGeometry } from 'ol/geom';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import { Input, Switch, SwitchChangeEvent } from '@progress/kendo-react-inputs';

import { MapMenu } from './map/map-menu';
import {
  GetAreasByZoneId,
  GetAreasWorldMap,
  GetZoneById,
  GetZonesByIds,
  GetZoneTitle,
  GetAreaTitle
} from '../../utils/game-document/assets';
import {
  getAllLocations,
  getPlayerLocationColorStatus
} from '../../utils/game-document/location';
import {
  getTopPoint,
  createCustomLabelPosition
} from '../../utils/game-engine/label-position';
import { PlayerState } from '../../types/state';
import { GameContext } from '../../contexts/game';
import { TeamsMenu } from './map-view-menu/teams';
import { getGroupList } from '../../services/groups';
import { PlayersMenu } from './map-view-menu/players';
import HelpSupport from '../../components/help-support';
import { useGameTask } from '../../hooks/use-game-task';
import { zoneAreasStyle } from '../../utils/map-helper';
import { roundingDecimalNumber } from '../../utils/number';
import { MapAdapter } from '../../components/map/mapAdapter';
import { MapContext } from '../../components/map/map-context';
import { LayoutContext } from '../../contexts/layout-provider';
import { GameDocumentContext } from '../../contexts/game-document';
import { FilterView, Menu } from '../../types/facilitator/map-menu';
import { GroupResponse } from '../../types/responses/group-response';
import { GetResourceValue } from '../../utils/game-document/resources';
import MapViewFilter from '../../components/facilitator/map-view-filter';
import { AreaEntity, MapEntity } from '../../types/game-document/entities';
import { DefaultZoomLevel, OfflinePlayerColor } from '../../constants/map';
import { Zone, ZoneAssetId } from '../../types/game-document/entities/zone';
import { useGamePlayerPosition } from '../../hooks/use-game-player-position';
import { MapIllustrationContext } from '../../components/map/map-illustration-context';

export type FilterKey = 'tasks' | 'players' | 'offlinePlayers';

export interface FilterState extends Record<FilterKey, boolean> {}

const INITIAL_FILTERS: FilterState = {
  tasks: true,
  players: true,
  offlinePlayers: true
};

export const MapView: React.FC = () => {
  const [gameState] = React.useContext(GameContext);
  const [gameDocument] = React.useContext(GameDocumentContext);
  const { isStreetView, toggleIsStreetView } = React.useContext(LayoutContext);
  const map = React.useContext(MapContext);
  const mapIllustration = React.useContext(MapIllustrationContext);

  const [illustrationMapLoaded, setIllustrationMapLoaded] =
    React.useState<boolean>(false);
  const [selectedMap, setSelectedMap] = React.useState<string>('world-map');
  const [isExpand, setIsExpand] = React.useState<boolean>(false);

  const mapElement = React.useRef<HTMLDivElement>(null);

  const objectLineColor: string | Color = gameDocument?.theme?.colors?.find(
    (x) => x.type === 'Info'
  )?.color ?? [255, 85, 39];

  const [vectorLayer] = React.useState(
    () =>
      new VectorLayer({
        source: new VectorSource(),
        style: (feature) => zoneAreasStyle(feature, objectLineColor)
      })
  );

  const filterData: FilterView[] = [
    { id: 'tasks', description: 'Tasks' },
    { id: 'players', description: 'Players' },
    { id: 'offline', description: 'Offline' }
  ];
  const [filterViewsSelected, setFilterViewSelected] =
    React.useState<FilterView[]>(filterData);
  const [filters, setFilters] = React.useState<FilterState>(INITIAL_FILTERS);

  const [selectedMenu, setSelectedMenu] = React.useState<Menu>();
  const [search, setSearch] = React.useState<string>('');
  const [menus, setMenus] = React.useState<Menu[]>([]);

  const locations = React.useMemo(() => {
    if (!gameDocument.gameDocument) return [];
    return getAllLocations(gameDocument.gameDocument);
  }, [gameDocument.gameDocument]);

  const currentMap = React.useMemo(() => {
    const gameDoc = gameDocument.gameDocument;
    let mapEntity: MapEntity | undefined = undefined;
    if (!selectedMap || !gameDoc) return undefined;

    if (selectedMap === 'world-map' || selectedMap === '') {
      const mapAsId = gameDoc?.rules.worldMap.mapAssId;
      mapEntity = gameDoc?.assets.maps?.find((m) => m.id === mapAsId);
    } else {
      const zone = gameDoc?.rules.worldMap.zones.find(
        (m) => m.zoneAssId === selectedMap
      );
      mapEntity = gameDoc?.assets.maps?.find((m) => m.id === zone?.mapAssId);
    }

    return mapEntity;
  }, [selectedMap]);

  const currentMapContext = React.useMemo(() => {
    return currentMap?.type === 'illustrated' ? mapIllustration : map;
  }, [currentMap?.type, map, mapIllustration]);

  const { addAllPlayer, addAllPlayerOnIllustration } = useGamePlayerPosition({
    mapType: currentMap?.type ?? 'googleMaps'
  });
  const { populateTaskOverlay } = useGameTask({
    mapType: currentMap?.type ?? 'googleMaps',
    selectedFacilitatorMap: selectedMap,
    isFacilitator: true
  });

  const getPlayerLocation = (playerState: PlayerState) => {
    if (playerState.location === '' || !playerState?.location) {
      return '--';
    } else if (playerState.location === 'world-map') {
      return 'world-map';
    } else {
      return (
        GetZoneTitle(gameDocument?.gameDocument, playerState.location) || '--'
      );
    }
  };

  const mapImages = React.useMemo(() => {
    const gameDoc = gameDocument.gameDocument;
    if (!gameDoc) return [];

    if (selectedMap === 'world-map') {
      return (
        gameDoc.assets?.mapImages?.filter(
          (item) => item.mapId === gameDoc.rules?.worldMap?.mapAssId!
        ) ?? []
      );
    } else {
      return gameDoc.assets?.mapImages?.filter(
        (item) =>
          item.mapId ===
            gameDoc.rules.worldMap.zones.find(
              (zone: ZoneAssetId) => zone.zoneAssId === selectedMap
            )?.mapAssId || ''
      );
    }
  }, [gameDocument.gameDocument, selectedMap]);

  const imageList = React.useMemo(() => {
    const gameDoc = gameDocument.gameDocument;
    if (!gameDoc) return [];
    const result: any[] = [];

    if (mapImages && mapImages?.length > 0) {
      mapImages.forEach((image) => {
        if (image.imageResId) {
          const url = GetResourceValue(gameDoc!, image.imageResId, '');
          result.push({
            id: `image-${image.id}`,
            url,
            opacity: image.opacity ?? 1,
            extent: image.extent,
            isVisible: image.isVisible
          });
        }
      });
    }

    return result;
  }, [mapImages, gameDocument.gameDocument, selectedMap]);

  const onGISMapChange = (e: SwitchChangeEvent) => {
    const mapTypeChange = e.target.value;
    toggleIsStreetView();

    const mapType = mapTypeChange ? 'googleMaps' : 'satelliteMap';
    const adapter = new MapAdapter({ type: mapType } as MapEntity);
    const tileLayer = adapter.baseLayer;

    const layers = map.getAllLayers();
    if (layers.length > 0) {
      layers[0] = tileLayer;
      map.setLayers(layers);
    } else {
      map.setLayers([tileLayer]);
    }

    map.getView();
  };

  React.useEffect(() => {
    if (!gameState.gameState?.players.length) return;
    if (currentMap?.type === 'illustrated') return;
    addAllPlayer(true);
  }, [
    currentMap?.type,
    gameState.gameState?.players.map((player) => player.code).join('')
  ]);

  React.useEffect(() => {
    addAllPlayerOnIllustration(true);
  }, [
    currentMap?.type,
    gameState.gameState?.teams,
    gameState.gameState?.players
  ]);

  React.useEffect(() => {
    (async () => {
      // Fetch group list
      const groupsResult = (await getGroupList(
        gameState?.gameCode!
      )) as GroupResponse[];
      const groups =
        groupsResult?.map((group) => ({
          name: group?.name,
          code: group?.code,
          type: 'group',
          score: ''
        })) || [];

      // Extract teams
      const teams =
        gameState?.gameState?.teams
          ?.map((team) => ({
            name: team.name,
            code: team.code,
            type: 'team',
            score: roundingDecimalNumber(team?.score ?? 0, 2) ?? ''
          }))
          ?.sort((a, b) => b.score - a.score) || [];

      // Extract players
      const players =
        gameState?.gameState?.players
          ?.map((player) => ({
            name: player.name,
            code: player.code,
            type: 'player',
            score: roundingDecimalNumber(player?.score ?? 0, 2) ?? '',
            statusColor: getPlayerLocationColorStatus(
              gameState?.gameState?.players?.find(
                (x) => x.code === player?.code
              )?.lastUpdated
            ),
            location: getPlayerLocation(player),
            locationId: player.location
          }))
          ?.sort((a, b) => b.score - a.score) || [];

      // Merge all items into a single array
      const menuItems = [...groups, ...teams, ...players];
      setMenus(menuItems as Menu[]);
    })();
  }, []);

  // render images
  React.useEffect(() => {
    const layers = currentMapContext.getAllLayers();
    const existingImageLayers = layers.filter((layer) =>
      layer.get('id')?.startsWith('image-')
    );
    existingImageLayers.forEach((layer) =>
      currentMapContext.removeLayer(layer)
    );

    if (
      !currentMapContext ||
      (currentMap?.type === 'illustrated' && !illustrationMapLoaded)
    )
      return;
    if (!imageList || imageList.length === 0) return;

    const imageLayers: ImageLayer<ImageStatic>[] = [];

    // wait for all images to load before adding layers
    const promises = imageList.map(
      (imgData) =>
        new Promise<void>((resolve) => {
          const { id, url, extent, opacity = 1, isVisible } = imgData;

          const image = new Image();
          image.src = url;
          image.onload = () => {
            const layer = new ImageLayer({
              source: new ImageStatic({
                url: url,
                imageExtent: extent ?? [0, 0, 0, 0]
              }),
              properties: { id },
              opacity,
              visible: isVisible
            });

            imageLayers.push(layer);
            resolve();
          };

          image.onerror = () => {
            console.warn(`Failed to load image: ${url}`);
            resolve();
          };
        })
    );

    // once all images are loaded, update the layer order
    Promise.all(promises).then(() => {
      if (imageLayers.length > 0) {
        const tileLayer = layers[0];
        const otherLayers = layers
          .slice(1)
          .filter((layer) => !layer.get('id')?.startsWith('image-'));

        // new order: tile → images → others
        currentMapContext.setLayers([
          tileLayer,
          ...imageLayers,
          ...otherLayers
        ]);
      }
    });
  }, [
    selectedMap,
    currentMap?.type,
    currentMapContext,
    illustrationMapLoaded,
    imageList.map((i) => i.id + i.url.substring(0, 20)).join('')
  ]);

  // render zones and areas
  React.useEffect(() => {
    const source = vectorLayer.getSource();
    const gameDoc = gameDocument.gameDocument;
    if (!source) return;
    if (!gameDoc) return;
    source.clear();

    const location = selectedMap;
    if (!location) return;

    let areas: AreaEntity[] = [];
    if (location === 'world-map') {
      areas = GetAreasWorldMap(gameDoc);
    } else {
      areas = GetAreasByZoneId(gameDoc, location);
    }

    areas.forEach((area) => {
      if (area.boundary) {
        const feature = new GeoJSON().readFeature(area.boundary) as Feature;
        feature.set('isVisible', area.isVisible);
        source.addFeature(feature);

        if (area.isVisible && area.isTitleVisible && area.boundary) {
          const id = area.boundary.id ?? '';
          const title = GetAreaTitle(gameDoc, area.id);

          const labelElement = createCustomLabelPosition(id, title);

          const overlay = new Overlay({
            id,
            position: getTopPoint(
              (feature.getGeometry() as Polygon).getCoordinates()[0] as any
            ),
            positioning: 'center-center',
            element: labelElement,
            stopEvent: false
          });

          currentMapContext.addOverlay(overlay);
        }
      }
    });

    const renderZone = (zone: Zone) => {
      const feature = new GeoJSON().readFeature(zone.boundary) as Feature;
      feature.set('isVisible', zone.isVisible);
      source.addFeature(feature);

      if (zone.isVisible && zone.isTitleVisible && zone.boundary) {
        const id = zone.boundary.id ?? '';
        const title = GetZoneTitle(gameDoc, zone.id);

        const labelElement = createCustomLabelPosition(id, title);

        const overlay = new Overlay({
          id,
          position: getTopPoint(
            (feature.getGeometry() as Polygon).getCoordinates()[0] as any
          ),
          positioning: 'center-center',
          element: labelElement,
          stopEvent: false
        });

        currentMapContext.addOverlay(overlay);
      }

      if (zone.id === selectedMap) {
        const geo = feature.getGeometry() as SimpleGeometry;
        if (geo)
          map.getView().fit(geo, {
            duration: 1000
          });
      }
    };

    if (location === 'world-map') {
      const zoneIds = gameDoc.rules?.worldMap?.zones?.map(
        (zone) => zone.zoneAssId
      );

      if (zoneIds) {
        const worldZones = GetZonesByIds(gameDoc, zoneIds);

        worldZones.forEach((zone) => {
          zone.boundary && renderZone(zone);
        });
      }
    } else {
      const zone = gameDoc.rules.worldMap.zones.find(
        (m) => m.zoneAssId === location
      );
      const zoneEntity = gameDoc.assets.zones?.find(
        (m) => m.id === zone?.zoneAssId
      );

      zoneEntity && zoneEntity.boundary && renderZone(zoneEntity);
    }
  }, [selectedMap, vectorLayer, gameDocument.gameDocument]);

  // render base map
  React.useLayoutEffect(() => {
    const gameDoc = gameDocument.gameDocument;
    if (!currentMap || !gameDoc) return;

    if (currentMap.type !== 'illustrated') {
      mapIllustration.setTarget(undefined);
      map.setTarget(mapElement.current as HTMLElement);
      map
        .getView()
        .setCenter(
          fromLonLat([currentMap.longitude ?? 0, currentMap.latitude ?? 0])
        );
      map.getView().setZoom(currentMap.zoomLevel ?? DefaultZoomLevel);
      populateTaskOverlay();

      if (!map.getLayers().getArray().includes(vectorLayer))
        map.addLayer(vectorLayer);
    } else {
      map.setTarget(undefined);
      const mapImage = GetResourceValue(gameDoc!, currentMap.imageResId!, '');
      const img = new Image();
      img.src = mapImage;
      img.onload = () => {
        const extent = [0, 0, img?.width!, img?.height!];
        const projection = new Projection({
          code: 'xkcd-image',
          units: 'pixels',
          extent: extent
        });

        mapIllustration.setView(
          new View({
            projection: projection,
            center: getCenter(extent),
            extent,
            zoom: 1
          })
        );

        const imageLayer = new ImageLayer({
          source: new Static({
            attributions: '',
            url: mapImage,
            projection: projection,
            imageExtent: extent
          })
        });

        let layers = mapIllustration.getAllLayers();
        mapIllustration.setTarget(mapElement.current as HTMLElement);
        layers[0] = imageLayer;
        if (!mapIllustration.getLayers().getArray().includes(vectorLayer)) {
          layers[1] = vectorLayer;
        }
        mapIllustration.setLayers(layers);
        mapIllustration
          .getView()
          .setCenter([currentMap.longitude ?? 0, currentMap.latitude ?? 0]);
        setIllustrationMapLoaded(true);
        populateTaskOverlay();
      };
    }

    return () => {
      map.setTarget(undefined);
      mapIllustration.setTarget(undefined);
      vectorLayer.getSource()?.clear();
    };
  }, [currentMap, vectorLayer]);

  return (
    <div className={'d-flex flex-column gap-2 h-100'}>
      <Row>
        <Col>
          <div className={'d-flex align-items-start flex-column'}>
            <label className={'fw-bold text-dark'}>Select map</label>
          </div>
          <DropDownList
            data={locations}
            textField={'name'}
            dataItemKey={'id'}
            value={locations?.find((x) => x.id === selectedMap)}
            className={'w-20 border mr-2'}
            onChange={(e) => {
              setSelectedMap(e.value.id);
            }}
          />
        </Col>
        <Col>
          <HelpSupport
            title="Map view guide"
            url="https://forum.catalystglobal.com/t/4621"
          />
        </Col>
      </Row>

      <Row>
        <Col>
          <div className={'d-flex align-items-center'}>
            <Input
              placeholder={'Search..'}
              value={search}
              className={'w-20 border mr-2'}
              onChange={(e) => setSearch((e?.target?.value as string) ?? '')}
            />
            <span
              style={{ fontSize: 'xx-large' }}
              className="material-symbols-outlined text-dark cursor-pointer"
              onClick={() => setIsExpand(!isExpand)}>
              {isExpand ? 'expand_circle_down' : 'menu'}
            </span>
          </div>
        </Col>
        <Col>
          <div className={'d-flex justify-content-end align-items-center'}>
            <MapViewFilter
              filters={filters}
              selectedPlayer={
                selectedMenu?.type === 'player'
                  ? (gameState.gameState?.players ?? []).find(
                      (player) => player.code === selectedMenu.code
                    )
                  : undefined
              }
              setFilters={setFilters}
            />
          </div>
        </Col>
      </Row>

      <div className={'layout-facilitator-map-container h-100'}>
        <div className={`map-menu w-20 ${!isExpand ? 'd-none' : ''}`}>
          <TeamsMenu
            search={search}
            selectedMenu={selectedMenu}
            setSelectedMenu={setSelectedMenu}
          />
          <PlayersMenu
            search={search}
            selectedMenu={selectedMenu}
            setSelectedMenu={setSelectedMenu}
          />
        </div>

        {currentMap?.type !== 'illustrated' && (
          <div className="map-player-location">
            <div className="list-group w-20 bg-transparent">
              <small className="p-2">Players in this location</small>
              {menus
                ?.filter(
                  (x) =>
                    x.name?.toLowerCase()?.includes(search?.toLowerCase()) &&
                    x.type === 'player' &&
                    x.locationId === selectedMap
                )
                ?.map((menu) => {
                  if (
                    filters.players ||
                    (!filters.players &&
                      filters.offlinePlayers &&
                      menu.statusColor?.toString() === OfflinePlayerColor)
                  ) {
                    return (
                      <MapMenu
                        key={menu.code}
                        menu={menu}
                        selectedMenu={selectedMenu}
                        showLocation={false}
                      />
                    );
                  } else {
                    return <React.Fragment key={menu.code} />;
                  }
                })}
            </div>
          </div>
        )}

        <div className={`layout-facilitator--mapview`} ref={mapElement}>
          {currentMap?.type !== 'illustrated' && (
            <div className={'map-view-option'}>
              <span>Street View</span>
              <Switch
                size={'small'}
                onLabel={'Street View'}
                offLabel={'Satelite View'}
                onChange={(e) => onGISMapChange(e)}
                checked={isStreetView}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
